/**
 * Type guards for error handling
 * Use these instead of `any` in catch blocks
 */

import type { AxiosError } from '../types/errorGuards.types'

export type { AxiosError }

/**
 * Standard Error type guard
 */
export function isError(error: unknown): error is Error {
  return error instanceof Error
}

/**
 * Type guard for errors with a message property
 */
export function isErrorWithMessage(
  error: unknown,
): error is { message: string } {
  return (
    error !== null &&
    typeof error === 'object' &&
    'message' in error &&
    typeof (error as { message: unknown }).message === 'string'
  )
}

/**
 * Type guard for errors with a reason property (common in some APIs)
 */
export function isErrorWithReason(error: unknown): error is { reason: string } {
  return (
    error !== null &&
    typeof error === 'object' &&
    'reason' in error &&
    typeof (error as { reason: unknown }).reason === 'string'
  )
}

/**
 * Type guard for Axios errors
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return (
    error !== null &&
    typeof error === 'object' &&
    ('response' in error || 'request' in error || 'config' in error)
  )
}

/**
 * Extract a user-friendly error message from any error type
 * Note: This is a more comprehensive version that handles Axios errors,
 * reason properties, etc. The simpler version in errorUtils.ts is kept
 * for backward compatibility.
 */
export function getErrorMessageDetailed(error: unknown): string {
  if (isError(error)) {
    return error.message
  }

  if (isErrorWithMessage(error)) {
    return error.message
  }

  if (isErrorWithReason(error)) {
    return error.reason
  }

  if (isAxiosError(error) && error.response?.data) {
    // Try to extract message from axios error response
    const data = error.response.data
    if (typeof data === 'string') {
      return data
    }
    if (
      data &&
      typeof data === 'object' &&
      'message' in data &&
      typeof (data as { message: unknown }).message === 'string'
    ) {
      return (data as { message: string }).message
    }
  }

  if (isAxiosError(error) && error.message) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'An unknown error occurred'
}

/**
 * Safely stringify an error for logging
 */
export function stringifyError(error: unknown): string {
  try {
    if (isError(error)) {
      return `${error.name}: ${error.message}\n${error.stack || ''}`
    }

    return JSON.stringify(error, null, 2)
  } catch {
    return String(error)
  }
}
